import React, { useState, useEffect } from "react";
import { Pause, Play, X } from "lucide-react";

/**
 * TickerBar — static headline, crossfade-rotates every 8s (redesign item 1).
 * No continuous marquee. PAUSE toggles rotation; ✕ dismisses for the session.
 */
const ROTATE_MS = 8000;

const TickerBar = ({ config, darkMode, dismissed = false, onDismiss }) => {
  const { items = [], is_enabled = false } = config || {};
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [selfDismissed, setSelfDismissed] = useState(false);
  const dm = darkMode;
  const hidden = dismissed || selfDismissed;

  useEffect(() => {
    if (!is_enabled || hidden || paused || items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [is_enabled, hidden, paused, items.length]);

  if (!is_enabled || !items.length || hidden) return null;
  const item = items[idx % items.length];

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
    else setSelfDismissed(true);
  };

  return (
    <div
      data-testid="ticker-bar"
      className="sticky top-0 z-[60]"
      style={{ height: 32, background: dm ? "#0c1011" : "#101c20", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-full flex items-center gap-3">
        <span className="flex-shrink-0 flex items-center gap-1.5 category-tag pr-3 border-r border-white/15" style={{ color: "#5ec6d2" }}>
          <span className="w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: "#5ec6d2" }} />
          Live
        </span>
        <div className="flex-1 min-w-0">
          {item.url ? (
            <a
              key={idx}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ticker-text"
              className="block truncate category-tag hover:underline animate-fade-in"
              style={{ color: "#dfe8e9" }}
            >
              {item.text}
            </a>
          ) : (
            <span key={idx} data-testid="ticker-text" className="block truncate category-tag animate-fade-in" style={{ color: "#dfe8e9" }}>
              {item.text}
            </span>
          )}
        </div>
        <button
          data-testid="ticker-pause-btn"
          onClick={() => setPaused((p) => !p)}
          title={paused ? "Resume headline rotation" : "Pause headline rotation"}
          className="flex-shrink-0 flex items-center gap-1 category-tag text-slate-400 hover:text-white transition-colors"
        >
          {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          <span className="hidden sm:inline">{paused ? "Play" : "Pause"}</span>
        </button>
        <button
          data-testid="ticker-dismiss-btn"
          onClick={handleDismiss}
          title="Hide ticker for this session"
          className="flex-shrink-0 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default TickerBar;
